<script setup lang="ts">
import { myApps, myPerms } from '@yc/api-client'
import type { AppCard } from '@yc/api-client'
import { EmptyState, usePermissions, useSession } from '@yc/ui-kit'
import { computed, onMounted, ref } from 'vue'

const { me } = useSession()
const { can } = usePermissions()

const apps = ref<AppCard[]>([])
const permCount = ref(0)
const loading = ref(true)
const failed = ref<unknown>(null)

const greeting = computed(() => {
  const h = new Date().getHours()
  if (h < 6) return '夜深了'
  if (h < 12) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
})

onMounted(async () => {
  loading.value = true
  try {
    const [cards, perms] = await Promise.all([myApps(), myPerms('portal')])
    apps.value = cards
    permCount.value = perms.codes.length
  } catch (e) {
    failed.value = e
  } finally {
    loading.value = false
  }
})

function open(app: AppCard): void {
  window.location.assign(app.url)
}

function goTenants(): void {
  window.location.assign('/admin/tenants')
}
</script>

<template>
  <div v-loading="loading" class="yc-home">
    <header class="yc-home__hero">
      <h2 class="yc-home__greeting">{{ greeting }}，{{ me?.name || me?.username }}</h2>
      <p class="yc-home__sub">
        {{ me?.tenant?.name }}（{{ me?.tenant?.code }}）· {{ me?.username }}
      </p>
    </header>

    <section class="yc-home__kpis">
      <div class="yc-kpi">
        <span class="yc-kpi__label">所属企业</span>
        <span class="yc-kpi__value">{{ me?.tenant?.name ?? '—' }}</span>
      </div>
      <div class="yc-kpi">
        <span class="yc-kpi__label">已开通系统</span>
        <span class="yc-kpi__value yc-tabular">{{ apps.length }}</span>
      </div>
      <div class="yc-kpi">
        <span class="yc-kpi__label">我的权限点</span>
        <span class="yc-kpi__value yc-tabular">{{ permCount }}</span>
      </div>
      <div class="yc-kpi">
        <span class="yc-kpi__label">登录态</span>
        <span class="yc-kpi__value">{{ me?.must_change_pwd ? '待改密' : '正常' }}</span>
      </div>
    </section>

    <section class="yc-home__apps">
      <h3 class="yc-home__section-title">我的应用</h3>

      <el-alert
        v-if="failed"
        class="yc-home__error"
        type="error"
        :closable="false"
        show-icon
        title="应用列表加载失败"
      />

      <div v-else-if="apps.length" class="yc-apps">
        <button
          v-for="app in apps"
          :key="app.code"
          type="button"
          class="yc-app"
          :style="{ '--yc-app-color': app.color || 'var(--yc-brand)' }"
          @click="open(app)"
        >
          <span class="yc-app__code">{{ app.short_name || app.code }}</span>
          <span class="yc-app__name">{{ app.name }}</span>
          <span class="yc-app__desc">{{ app.description }}</span>
        </button>
      </div>

      <EmptyState
        v-else-if="!loading"
        title="暂无可访问的系统"
        description="当前账号尚未被授予任何子系统的权限。如需使用请联系企业管理员或平台运营方开通。"
      >
        <el-button v-if="can('portal:tenant')" type="primary" @click="goTenants">
          前往租户管理
        </el-button>
      </EmptyState>
    </section>
  </div>
</template>

<style scoped lang="scss">
.yc-home {
  &__hero {
    margin-bottom: var(--yc-space-6);
  }

  &__greeting {
    margin: 0;
    font-size: var(--yc-font-2xl);
    font-weight: 600;
  }

  &__sub {
    margin: var(--yc-space-2) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font);
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--yc-space-4);
    margin-bottom: var(--yc-space-8);
  }

  &__apps {
    min-height: 240px;
  }

  &__section-title {
    margin: 0 0 var(--yc-space-4);
    font-size: var(--yc-font-lg);
    font-weight: 600;
  }

  &__error {
    margin-bottom: var(--yc-space-4);
  }
}

.yc-kpi {
  display: flex;
  flex-direction: column;
  gap: var(--yc-space-2);
  padding: var(--yc-space-4);
  background: var(--yc-card);
  border: 1px solid var(--yc-border);
  border-radius: var(--yc-radius);
  box-shadow: var(--yc-shadow-sm);

  &__label {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__value {
    font-size: var(--yc-font-xl);
    font-weight: 600;
  }
}

.yc-apps {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--yc-space-4);
}

.yc-app {
  display: flex;
  flex-direction: column;
  gap: var(--yc-space-2);
  padding: var(--yc-space-4);
  text-align: left;
  cursor: pointer;
  background: var(--yc-card);
  border: 1px solid var(--yc-border);
  border-top: 3px solid var(--yc-app-color, var(--yc-brand));
  border-radius: var(--yc-radius);
  box-shadow: var(--yc-shadow-sm);
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover,
  &:focus-visible {
    box-shadow: var(--yc-shadow);
    transform: translateY(-1px);
  }

  &__code {
    align-self: flex-start;
    padding: 1px 6px;
    color: var(--yc-text-secondary);
    font-family: var(--yc-font-mono);
    font-size: var(--yc-font-xs);
    background: var(--yc-sunken);
    border-radius: var(--yc-radius-sm);
  }

  &__name {
    font-size: var(--yc-font-lg);
    font-weight: 600;
  }

  &__desc {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
    line-height: 1.6;
  }
}
</style>